import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useIsFetching, useIsMutating, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useClinic } from '@/contexts/ClinicContext';
import { useAuth } from '@/contexts/AuthContext';
import { EncounterProvider, EncounterContextValue } from '@/contexts/EncounterContext';
import VisitCockpitShell, { type CockpitSaveState } from '@/components/visit/VisitCockpitShell';
import { COCKPIT_NAV_ORDER, resolveSection, type CockpitSectionId } from '@/components/visit/cockpit-nav';
import AvsPreviewPanel from '@/components/visit/AvsPreviewPanel';
import SignEncounterDialog from '@/components/visit/SignEncounterDialog';
import CockpitCommandPalette from '@/components/visit/CockpitCommandPalette';
import RoomingChecklistPanel from '@/components/visit/RoomingChecklistPanel';
import EncounterContextRail from '@/components/visit/EncounterContextRail';
import PreVisitSummaryCard from '@/components/visit/PreVisitSummaryCard';
import ReconcileAllergiesDialog from '@/components/visit/ReconcileAllergiesDialog';
import ReconcileMedicationsDialog from '@/components/visit/ReconcileMedicationsDialog';
import VitalsCaptureSheet from '@/components/emr/VitalsCaptureSheet';
import ChartPanel from '@/components/visit/ChartPanel';
import PlanPanel from '@/components/visit/PlanPanel';
import PatientContextPanel from '@/components/visit/PatientContextPanel';
import IntakeResponsePanel from '@/components/visit/IntakeResponsePanel';
import { emrService, EmrClinicalNote } from '@/services/emrService';
import { intakeService } from '@/services/intakeService';
import { allergiesService } from '@/services/allergiesService';
import { medicationsService } from '@/services/medicationsService';
import { problemsService } from '@/services/problemsService';
import { patientService } from '@/services/patientService';
import { getEncounterLabOrders } from '@/services/labService';
import { vitalsService, VitalLoinc, type VitalObservation } from '@/services/vitalsService';
import { useEncounter } from '@/contexts/EncounterContext';
import { cacheKeys, cacheRoots } from '@/lib/cacheKeys';

/**
 * Lab order statuses that mean "the order is in flight at the lab provider
 * but not yet finalized" — i.e. results haven't started coming back. Sign
 * is still allowed (labs commonly result days after sign), but the cockpit
 * surfaces these counts as a soft warning so the provider knows the chart
 * isn't fully closed yet.
 */
const PENDING_PROVIDER_STATUSES = new Set([
  'order_created',
  'pending',
  'requisition_ready',
  'appointment_pending',
  'pending_appointment',
  'appointment_scheduled',
  'scheduled',
]);

const VisitCockpit: React.FC = () => {
  const { encounterId } = useParams<{ encounterId: string }>();
  const navigate = useNavigate();
  const { clinic } = useClinic();
  const { user } = useAuth();
  const [section, setSection] = useState<CockpitSectionId>('overview');
  const [contextTab, setContextTab] = useState<'vitals' | 'allergies' | 'medications'>('vitals');
  const jumpToContext = (tab: 'vitals' | 'allergies' | 'medications') => {
    setContextTab(tab);
    setSection('context');
  };
  const [showSign, setShowSign] = useState(false);
  const [showCmd, setShowCmd] = useState(false);
  const [notesPage, setNotesPage] = useState(1);
  const [railShowAllergies, setRailShowAllergies] = useState(false);
  const [railShowMeds, setRailShowMeds] = useState(false);
  const [railShowVitals, setRailShowVitals] = useState(false);
  const qc = useQueryClient();

  const clinicId = clinic?.id || '';

  const { data: encounter, isLoading: loadingEncounter, refetch: refetchEncounter } = useQuery({
    queryKey: cacheKeys.encounter(encounterId || ''),
    queryFn: () => emrService.getEncounter(clinicId, encounterId!),
    enabled: !!clinicId && !!encounterId,
  });

  const { data: appointment } = useQuery({
    queryKey: cacheKeys.appointment(encounter?.appointment_id || ''),
    queryFn: () => emrService.getAppointment(clinicId, encounter!.appointment_id!),
    enabled: !!clinicId && !!encounter?.appointment_id,
  });

  const { data: patient } = useQuery({
    queryKey: ['patient', encounter?.patient_id, 'detail'],
    queryFn: () => patientService.getPatientById(encounter!.patient_id),
    enabled: !!encounter?.patient_id,
  });

  // Sidebar badges (active counts).
  const { data: allergies = [] } = useQuery({
    queryKey: cacheKeys.patientAllergies(encounter?.patient_id || '', { activeOnly: true }),
    queryFn: () => allergiesService.getPatientAllergies(clinicId, encounter!.patient_id, { activeOnly: true }),
    enabled: !!encounter?.patient_id,
  });
  const { data: meds = [] } = useQuery({
    queryKey: cacheKeys.patientMedications(encounter?.patient_id || '', { activeOnly: true }),
    queryFn: () => medicationsService.getPatientMedications(clinicId, encounter!.patient_id, { activeOnly: true }),
    enabled: !!encounter?.patient_id,
  });
  const { data: conditions = [] } = useQuery({
    queryKey: cacheKeys.patientConditions(encounter?.patient_id || ''),
    queryFn: () => problemsService.getPatientConditions(clinicId, encounter!.patient_id),
    enabled: !!encounter?.patient_id,
  });
  const { data: notesData, refetch: refetchNotes } = useQuery({
    queryKey: cacheKeys.patientNotes(encounter?.patient_id || '', notesPage),
    queryFn: () => emrService.getPatientNotesPaginated(clinicId, encounter!.patient_id, {
      page: notesPage, limit: 20,
    }),
    enabled: !!encounter?.patient_id,
  });

  // Lab orders attached to this encounter, used by the sign-warning
  // hint and (incidentally) primes the LinkedLabOrdersList cache.
  const { data: encounterLabOrders = [] } = useQuery({
    queryKey: cacheKeys.encounterLabOrders(encounterId || ''),
    queryFn: () => getEncounterLabOrders(clinicId, encounterId!),
    enabled: !!clinicId && !!encounterId,
  });

  const encounterDxCount = conditions.filter(c => c.category === 'encounter-diagnosis').length;
  const encounterNotes: EmrClinicalNote[] = (notesData?.data || []).filter(
    n => n.encounter_id === encounterId,
  );

  // Pre-visit intake status for the cockpit Intake tab badge + sign warning.
  const apptIdForIntake = encounter?.appointment_id || null;
  const { data: intakeListRes } = useQuery({
    queryKey: ['intakes', 'by-appointment', apptIdForIntake],
    queryFn: () => intakeService.getIntakesForAppointment(apptIdForIntake!),
    enabled: !!apptIdForIntake,
  });
  const intakesForAppt = (intakeListRes?.data || []) as Array<{
    status: string; questionnaire_response_id?: string | null;
  }>;
  const hasAnyIntake = intakesForAppt.length > 0;
  const intakeIncomplete = hasAnyIntake
    && intakesForAppt.every(i => i.status !== 'completed');
  const intakeCompletedUnreviewed = hasAnyIntake
    && intakesForAppt.some(i => i.status === 'completed');

  const ctxValue: EncounterContextValue | null = useMemo(() => {
    if (!encounter || !clinicId) return null;
    return {
      encounterId: encounter.id,
      appointmentId: encounter.appointment_id || null,
      patientId: encounter.patient_id,
      clinicId,
      encounter,
      appointment: appointment ?? null,
      isSigned: !!encounter.signed_at,
    };
  }, [encounter, appointment, clinicId]);

  // Cross-encounter save indicator. Watches React Query mutation state across
  // the whole app — any in-flight write surfaces "Saving…", and the indicator
  // briefly flashes "Saved" when activity drops to zero.
  const fetching = useIsFetching();
  const mutating = useIsMutating();
  const [saveState, setSaveState] = useState<CockpitSaveState>('idle');
  useEffect(() => {
    if (mutating > 0) {
      setSaveState('saving');
      return;
    }
    if (fetching === 0 && mutating === 0) {
      setSaveState((prev) => (prev === 'saving' ? 'saved' : prev));
    }
  }, [fetching, mutating]);
  useEffect(() => {
    if (saveState !== 'saved') return;
    const t = setTimeout(() => setSaveState('idle'), 2500);
    return () => clearTimeout(t);
  }, [saveState]);

  // Number-key tab switching: pressing 1..9 (no modifier) while not focused on
  // an input/textarea/contenteditable jumps to the corresponding tab. Skipped
  // while a modifier key is held so Cmd+K stays available.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (!/^[1-9]$/.test(e.key)) return;
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) return;
        if (target.getAttribute('role') === 'combobox' || target.getAttribute('role') === 'textbox') return;
      }
      const idx = Number.parseInt(e.key, 10) - 1;
      if (idx >= 0 && idx < COCKPIT_NAV_ORDER.length) {
        e.preventDefault();
        setSection(COCKPIT_NAV_ORDER[idx]);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  if (loadingEncounter || !ctxValue) {
    return (
      <div className='flex h-screen items-center justify-center'>
        <Loader2 className='h-8 w-8 animate-spin text-brand-teal' />
      </div>
    );
  }

  // Patient details / address for lab orders. Patient.addresses[] is the
  // canonical store (typed by `address_type`); the flat fields on the
  // patient row are usually empty.
  const patientAny = patient as any;
  const shippingAddr = patientAny?.addresses?.find((a: any) => a.address_type === 'shipping')
    || patientAny?.addresses?.find((a: any) => a.address_type === 'home')
    || patientAny?.addresses?.[0]
    || null;

  const patientDetails = {
    firstName: patientAny?.first_name || '',
    lastName: patientAny?.last_name || '',
    dob: patientAny?.dob || '',
    gender: (patientAny?.biological_sex || patientAny?.gender || 'unknown') as 'male' | 'female' | 'unknown',
    phone: patientAny?.phone || '',
    email: patientAny?.email || '',
  };
  const patientAddress = {
    firstLine: shippingAddr?.line_1 || patientAny?.address_line_1 || patientAny?.shipping_address_line_1 || '',
    secondLine: shippingAddr?.line_2 || patientAny?.address_line_2 || patientAny?.shipping_address_line_2 || undefined,
    city: shippingAddr?.city || patientAny?.city || patientAny?.shipping_city || '',
    state: shippingAddr?.stateInfo?.code || shippingAddr?.state_code || patientAny?.state || patientAny?.shipping_state || '',
    zip: shippingAddr?.postal_code || patientAny?.zip_code || patientAny?.shipping_zip_code || '',
  };

  // Sign button "incomplete" hints — the wizard still runs the full check;
  // these reasons just make the lack of progress visible up-front.
  const signIncompleteReasons: string[] = [];
  if (!encounter?.chief_complaint) signIncompleteReasons.push('No chief complaint');
  if (encounterDxCount === 0) signIncompleteReasons.push('No encounter diagnosis');
  if (encounterNotes.length === 0) signIncompleteReasons.push('No clinical note drafted');
  if (intakeIncomplete) signIncompleteReasons.push('Pre-visit intake not completed by patient');

  // Soft warning, not a block — labs commonly result days after sign.
  // The provider just needs to know the order chain isn't fully
  // closed-out at the lab vendor yet.
  const pendingLabCount = encounterLabOrders.filter(
    (o: any) => PENDING_PROVIDER_STATUSES.has(o.status),
  ).length;
  if (pendingLabCount > 0) {
    signIncompleteReasons.push(
      `${pendingLabCount} lab order${pendingLabCount === 1 ? '' : 's'} not yet finalized at provider`,
    );
  }

  const demographics = {
    dob: patientAny?.dob,
    sex: patientAny?.biological_sex || patientAny?.gender,
    mrn: patientAny?.mrn,
  };

  return (
    <EncounterProvider value={ctxValue}>
      <VisitCockpitShell
        active={section}
        onChange={setSection}
        onSign={() => setShowSign(true)}
        navBadges={{
          notes: encounterNotes.length,
          diagnoses: encounterDxCount,
          allergies: allergies.length,
          medications: meds.length,
          // Intake badge: '!' when sent but not completed; '✓' when completed.
          // Shell expects a number — encode 1 for both signals; visual styling
          // can layer onto this later. Suppress entirely when no intake exists.
          intake: intakeIncomplete || intakeCompletedUnreviewed ? 1 : 0,
        }}
        demographics={demographics}
        onOpenCommandPalette={() => setShowCmd(true)}
        saveState={saveState}
        signIncompleteReasons={signIncompleteReasons}
        rightRail={
          <EncounterContextRail
            onJumpToAllergies={() => jumpToContext('allergies')}
            onJumpToMedications={() => jumpToContext('medications')}
            onJumpToProblems={() => setSection('chart')}
            onJumpToVitals={() => jumpToContext('vitals')}
            onReconcileAllergies={() => setRailShowAllergies(true)}
            onReconcileMedications={() => setRailShowMeds(true)}
            onCaptureVitals={() => setRailShowVitals(true)}
          />
        }
      >
        {section === 'overview' && (
          <OverviewPanel
            encounter={encounter}
            allergies={allergies}
            meds={meds}
            conditions={conditions}
            notes={encounterNotes}
          />
        )}
        {section === 'chart' && (
          <ChartPanel
            notes={encounterNotes}
            pagination={{
              page: notesPage,
              limit: 20,
              total: notesData?.pagination?.total ?? 0,
              totalPages: notesData?.pagination?.totalPages ?? 1,
            }}
            onPaginationChange={setNotesPage}
            refetch={() => refetchNotes()}
            patientId={ctxValue.patientId}
          />
        )}
        {section === 'plan' && (
          <PlanPanel
            practitionerId={(user as any)?.practitionerId || ''}
            patientDetails={patientDetails}
            patientAddress={patientAddress}
          />
        )}
        {section === 'context' && (
          <PatientContextPanel
            needsReconciliation={(patientAny as any)?.allergies_need_reconciliation}
            tab={contextTab}
            onTabChange={setContextTab}
          />
        )}
        {section === 'intake' && <IntakeResponsePanel />}
        {section === 'avs' && <AvsPreviewPanel />}
      </VisitCockpitShell>

      <SignEncounterDialog
        open={showSign}
        onOpenChange={setShowSign}
        onSigned={() => {
          refetchEncounter();
        }}
      />

      <CockpitCommandPalette
        open={showCmd}
        onOpenChange={setShowCmd}
        onJump={(id) => setSection(resolveSection(id))}
        onSign={() => setShowSign(true)}
        isSigned={ctxValue.isSigned}
      />

      {/* Reconciliation dialogs opened from the right-rail actions. The
          existing Rooming panel renders its own copies, but the rail can
          fire from any section, so they live at the cockpit level too. */}
      <ReconcileAllergiesDialog
        open={railShowAllergies}
        onOpenChange={setRailShowAllergies}
        clinicId={ctxValue.clinicId}
        patientId={ctxValue.patientId}
        encounterId={ctxValue.encounterId}
        onCompleted={() => {
          qc.invalidateQueries({ queryKey: cacheRoots.patientAllergies(ctxValue.patientId) });
        }}
      />
      <ReconcileMedicationsDialog
        open={railShowMeds}
        onOpenChange={setRailShowMeds}
        clinicId={ctxValue.clinicId}
        patientId={ctxValue.patientId}
        encounterId={ctxValue.encounterId}
        onCompleted={() => {
          qc.invalidateQueries({ queryKey: cacheRoots.patientMedications(ctxValue.patientId) });
        }}
      />
      <VitalsCaptureSheet
        open={railShowVitals}
        onOpenChange={setRailShowVitals}
        clinicId={ctxValue.clinicId}
        encounterId={ctxValue.encounterId}
        patientId={ctxValue.patientId}
        onRecorded={() => {
          setRailShowVitals(false);
          qc.invalidateQueries({ queryKey: cacheRoots.patientVitals(ctxValue.patientId) });
          qc.invalidateQueries({ queryKey: cacheKeys.encounterVitals(ctxValue.encounterId) });
        }}
      />
    </EncounterProvider>
  );
};

const OverviewPanel: React.FC<{
  encounter: any;
  allergies: any[];
  meds: any[];
  conditions: any[];
  notes: EmrClinicalNote[];
}> = ({ encounter, allergies, meds, conditions, notes }) => {
  const activeProblems = conditions.filter(c => c.category === 'problem-list-item' && c.status === 'active');
  return (
    <div className='space-y-4'>
    <PreVisitSummaryCard />
    {/* Rooming was its own tab pre-Phase-4; folded into Overview here so the
        practitioner sees the start-of-visit checklist on the landing tab. */}
    <RoomingChecklistPanel />
    <div className='grid gap-4 md:grid-cols-2'>
      <Tile title='Chief complaint'>
        <p className='text-sm'>{encounter?.chief_complaint || 'Not yet recorded'}</p>
      </Tile>
      <Tile title='Vitals'>
        <EncounterVitalsTile />
      </Tile>
      <Tile title='Active allergies'>
        {allergies.length === 0 ? (
          <p className='text-sm text-slate-500'>No known allergies</p>
        ) : (
          <ul className='text-sm space-y-1'>
            {allergies.slice(0, 5).map(a => <li key={a.id}>{a.code_display}</li>)}
          </ul>
        )}
      </Tile>
      <Tile title='Active medications'>
        {meds.length === 0 ? (
          <p className='text-sm text-slate-500'>None</p>
        ) : (
          <ul className='text-sm space-y-1'>
            {meds.slice(0, 5).map(m => <li key={m.id}>{m.medication_display}</li>)}
            {meds.length > 5 && <li className='text-xs text-slate-500'>+{meds.length - 5} more</li>}
          </ul>
        )}
      </Tile>
      <Tile title='Problem list'>
        {activeProblems.length === 0 ? (
          <p className='text-sm text-slate-500'>None</p>
        ) : (
          <ul className='text-sm space-y-1'>
            {activeProblems.slice(0, 5).map(c => <li key={c.id}>{c.display}</li>)}
          </ul>
        )}
      </Tile>
      <Tile title='Notes drafted this visit'>
        {notes.length === 0 ? (
          <p className='text-sm text-slate-500'>No notes yet</p>
        ) : (
          <ul className='text-sm space-y-1'>
            {notes.slice(0, 3).map(n => (
              <li key={n.id} className='truncate'>
                {n.note_type.toUpperCase()} — {n.is_signed ? 'signed' : 'draft'}
              </li>
            ))}
          </ul>
        )}
      </Tile>
    </div>
    </div>
  );
};

const Tile: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className='rounded-lg border border-slate-200 bg-white p-4'>
    <h3 className='mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500'>{title}</h3>
    {children}
  </div>
);

/**
 * Vitals tile for the visit overview — reads this encounter's observations.
 * (Prior implementation read denormalized `encounter.vitals.*` fields that
 * were dropped when vitals migrated to FHIRObservation, so the tile was
 * stuck on "Not yet captured" regardless of what had been saved.)
 */
const EncounterVitalsTile: React.FC = () => {
  const { clinicId, encounterId } = useEncounter();
  const { data: observations = [] } = useQuery({
    queryKey: cacheKeys.encounterVitals(encounterId),
    queryFn: () => vitalsService.getEncounterVitals(clinicId, encounterId),
  });

  // Pick the most recent observation per LOINC.
  const latest: Record<string, VitalObservation> = {};
  for (const obs of observations) {
    const existing = latest[obs.code_code];
    const ts = obs.effective_date_time ? new Date(obs.effective_date_time).getTime() : 0;
    const existingTs = existing?.effective_date_time
      ? new Date(existing.effective_date_time).getTime()
      : 0;
    if (!existing || ts > existingTs) latest[obs.code_code] = obs;
  }

  const bp = latest[VitalLoinc.BLOOD_PRESSURE_PANEL];
  const sys = bp?.components?.find(c => c.code === VitalLoinc.SYSTOLIC_BP)?.value;
  const dia = bp?.components?.find(c => c.code === VitalLoinc.DIASTOLIC_BP)?.value;
  const hr = latest[VitalLoinc.HEART_RATE]?.value_quantity_value;
  const temp = latest[VitalLoinc.BODY_TEMPERATURE]?.value_quantity_value;
  const wt = latest[VitalLoinc.BODY_WEIGHT];
  const spo2 = latest[VitalLoinc.OXYGEN_SATURATION]?.value_quantity_value;

  const rows: React.ReactNode[] = [];
  if (sys != null && dia != null) rows.push(<li key='bp'>BP: {sys}/{dia} mmHg</li>);
  if (hr != null) rows.push(<li key='hr'>HR: {hr} bpm</li>);
  if (temp != null) rows.push(<li key='temp'>Temp: {temp} °F</li>);
  if (wt?.value_quantity_value != null) {
    rows.push(<li key='wt'>Weight: {wt.value_quantity_value} {wt.value_quantity_unit || 'lb'}</li>);
  }
  if (spo2 != null) rows.push(<li key='spo2'>SpO₂: {spo2}%</li>);

  if (rows.length === 0) return <p className='text-sm text-slate-500'>Not yet captured</p>;
  return <ul className='text-sm space-y-1'>{rows}</ul>;
};

export default VisitCockpit;
